/**
 * The KAF Recorder on a meeting (Aziz, 2026-09-27): recorder@kaf4media.com joins every
 * Google Meet on its own and records the audio; KAF's transcriber turns it into text.
 * This card says where that stands and, for a general meeting, shows the transcript.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useIsAdmin } from '@/hooks/use-scopes';
import Button from '@/components/ui/Button';
import { AlertTriangle, ChevronDown, ChevronUp, Mic, RefreshCw } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/cn';
import { fetchMeetingReview, fetchRecording, retranscribe, type Meeting } from '@/lib/calls';
import { bidi, BIDI_CLS } from '@/lib/bidi';

export const RECORDER_LIVE = ['joining', 'recording', 'uploading'];

function clock(iso?: string | null) {
  return iso ? new Date(iso).toLocaleTimeString('en-GB', { timeZone: 'Asia/Dubai', hour: '2-digit', minute: '2-digit' }) : '';
}
function mmss(sec: number) {
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function line(m: Meeting): { text: string; tone: 'info' | 'live' | 'bad' } | null {
  const note = m.recorder_note ? ` (${m.recorder_note})` : '';
  switch (m.recorder_state) {
    case 'joining': return { text: 'KAF Minutes is joining the call.', tone: 'live' };
    case 'recording': return { text: `Recording since ${clock(m.recorder_at)} Dubai time.`, tone: 'live' };
    case 'uploading': return { text: 'The call has ended. Saving the recording.', tone: 'live' };
    case 'recorded':
      if (m.transcript_pending) return { text: `Recorded${note}. Transcribing on KAF's server; you are told when it is ready.`, tone: 'live' };
      return { text: `Recorded${note}.`, tone: 'info' };
    case 'no_one': return { text: 'Nobody spoke on the call, so there was nothing to record.', tone: 'info' };
    case 'refused': case 'signed_out': case 'failed':
      return { text: `Not recorded${note}. Upload the recording yourself if you have one.`, tone: 'bad' };
    default:
      if (['link_sent', 'booking', 'booked'].includes(m.status)) {
        return { text: 'KAF Minutes joins this Google Meet on its own and records it for the minutes. Everyone on the call sees it there.', tone: 'info' };
      }
      return null;
  }
}

export default function RecordingCard({ m, showTranscript, onChange }: { m: Meeting; showTranscript: boolean; onChange?: (n?: Meeting) => void }) {
  const [open, setOpen] = useState(false);
  // the kept recording, to check the transcript against while it is validated (2026-10-02)
  const isAdmin = useIsAdmin();
  const [listen, setListen] = useState(false);
  const rec = useQuery({ queryKey: ['meeting-recording', m.id], queryFn: () => fetchRecording(m.id), enabled: listen && isAdmin });
  const again = useMutation({
    mutationFn: () => retranscribe(m.id),
    onSuccess: (x) => { toast.success('Transcribing again. The new transcript and minutes replace these; the old transcript is kept.'); onChange?.(x); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Could not start'),
  });
  const review = useQuery({ queryKey: ['meeting-review', m.id], queryFn: () => fetchMeetingReview(m.id), enabled: open && !!m.has_transcript });
  if (!m.meet_uri || m.dry_run) return null;
  const l = line(m);
  if (!l && !m.has_transcript) return null;

  return (
    <Card data-testid="recording-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted mb-2"><Mic size={12} /> Recording</div>
      {l && (
        <p className={cn('flex items-start gap-2 text-sm', l.tone === 'bad' ? 'text-red-300' : l.tone === 'live' ? 'text-muted-strong' : 'text-muted')} data-testid="recorder-state">
          {l.tone === 'live' && <RefreshCw size={14} className="animate-spin mt-0.5 shrink-0" />}
          {l.tone === 'bad' && <AlertTriangle size={14} className="mt-0.5 shrink-0" />}
          <span>{l.text}</span>
        </p>
      )}
      {isAdmin && m.audio_kept && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="recording-kept">
          {!listen ? (
            <Button size="sm" variant="secondary" onClick={() => setListen(true)} data-testid="recording-listen">Listen to the recording</Button>
          ) : rec.data?.url ? (
            <audio controls src={rec.data.url} className="w-full max-w-md" data-testid="recording-audio" />
          ) : (
            <span className="text-xs text-muted">{rec.isLoading ? 'Loading the recording...' : 'The recording is not available.'}</span>
          )}
          {showTranscript && m.has_transcript && (
            <Button size="sm" variant="ghost" icon={<RefreshCw size={12} />} loading={again.isPending} onClick={() => again.mutate()} data-testid="recording-retranscribe">
              Transcribe again
            </Button>
          )}
        </div>
      )}
      {showTranscript && m.has_transcript && (
        <div className="mt-3">
          <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-sm text-accent-400 hover:underline" data-testid="toggle-transcript">
            {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {open ? 'Hide the transcript' : 'Read the transcript'}
          </button>
          {open && (
            <div className="mt-2 max-h-[60vh] overflow-y-auto rounded-lg border border-slate-800 divide-y divide-slate-800/60" data-testid="transcript">
              {review.isLoading && <div className="p-3 text-sm text-muted">Loading the transcript...</div>}
              {review.isError && <div className="p-3 text-sm text-red-300">The transcript is limited to the people running this meeting.</div>}
              {(review.data?.transcript ?? []).map((t, i) => (
                <div key={i} className="flex gap-3 px-3 py-1.5 text-sm">
                  <span className="text-[11px] text-muted tabular-nums pt-0.5 shrink-0">{mmss(t.start)}</span>
                  <span dir="auto" className={cn('text-muted-strong [overflow-wrap:anywhere]', BIDI_CLS)}>{bidi(t.text)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
